import axios from 'axios';
import { GiftCardPurchaseDetails, GiftCardPurchaseResult, GiftCardDeliveryOption, GiftCardPersonalMessage, GiftCardWrappingOption } from '@/services/models'
import { toCamelCaseProperties } from '@/utils/fn';


export default {
	/**
	 * Buy portal gift card
	 * @param {GiftCardPurchaseDetails} giftCardPurchaseDetails
	 */
	async BuyGiftCard(giftCardPurchaseDetails = new GiftCardPurchaseDetails()) {
		return new GiftCardPurchaseResult(
			(
				await axios.post('/api/ajax/PortalGiftCardApi/BuyPortalGiftCard', {
					giftCardPurchaseDetails
				})
			).data
		);
	},

	/**
	 * Confirm portal gift card payment
	 * @param {String} paymentIntentId
	 */
	async ConfirmGiftCardPayment(paymentIntentId, shoppingCartId = 0) {
		return new GiftCardPurchaseResult(
			(
				await axios.post('/api/ajax/PortalGiftCardApi/ConfirmPortalGiftCardPayment', {
					paymentIntentId,
					shoppingCartId
				})
			).data
		);
	},

	/**
	 * Check if a giftcard with the given id exists in the database
	 * @param {string} giftcardId either physical og external giftcard id
	 */
	async CheckValidCardId(giftcardId) {
		var res = await axios.get('/api/ajax/PortalGiftCardApi/ValidGiftcardId', {
			params: {
				giftcardId: giftcardId
			}
		});
		return res.data.IntegerValue;
	},

	/**
	 * Fetches all active giftcards for a fixit.no user by mobile number
	 * @param {number} onlineSluttkundeId Fixit.no profile user id
	 * @param {string} mobile Mobile number
	 */
	async GetActivatedPortalGiftCards(onlineSluttkundeId, mobile) {
		var res = await axios.get('/api/ajax/PortalGiftCardApi/GetActivePortalGiftCardsByMobile', {
			params: {
				onlinesluttkundeid: onlineSluttkundeId,
				mobile: mobile
			}
		});
		switch (res.data.ResponseCode) {
			case 200: {
				let giftcards = res.data.Array;
				giftcards = giftcards.map(giftcard => toCamelCaseProperties(giftcard));
				return giftcards;
			}
			case 204:
				return [];
			default:
				return res.data.ResponseMessage;
		}
	},
	async GetUnActivatedPortalGiftcardsByPhoneNumber(phoneNumber, onlineSluttkundeId) {
		var res = await axios.get('/api/ajax/PortalGiftCardApi/GetUnActivatedPortalGiftcardsByPhoneNumber', {
			params: {
				phoneNumber: phoneNumber,
				onlinesluttkundeid: onlineSluttkundeId
			}
		});
		switch (res.data.ResponseCode) {
			case 200: {
				let giftcards = res.data.Array;
				giftcards = giftcards.map(giftcard => toCamelCaseProperties(giftcard));
				return giftcards;
			}
			case 204:
				return [];
			default:
				return res.data.ResponseMessage;
		}
	},

	/**
	 * Fetches information about the activated giftcard
	 * @param {string} giftcardId either physical og external giftcard id
	 * @param {string} phoneNumber
	 */
	async ActivateGiftCard(giftcardId, phoneNumber) {
		var res = await axios.get('/api/ajax/PortalGiftCardApi/ActivateGiftCard', {
			params: {
				giftcardId: giftcardId,
				phoneNumber: phoneNumber
			}
		});
		switch (res.data.ResponseCode) {
			case 200:
				return res.data.Dto;
			default:
				return res.data.ResponseMessage;
		}
	},
	async GetPortalGiftCardPurchaseHistory(onlinesluttkundeid) {
		var res = await axios.get('/api/ajax/PortalGiftCardApi/GetPortalGiftCardPurchaseHistory', {
			params: {
				onlinesluttkundeid: onlinesluttkundeid
			}
		});
		switch (res.data.ResponseCode) {
			case 200: {
				let purchases = res.data.Array;
				purchases = purchases.map(purchase => toCamelCaseProperties(purchase));
				return purchases;
			}
			case 204:
				return [];
			default:
				return res.data.ResponseMessage;
		}
	},

	/**
	 * Get the available delivery options for portal gift cards.
	 * @returns {Promise<GiftCardDeliveryOption[]|boolean>} Array of delivery options, or `false` if fetching failed.
	 */
	async GetGiftCardDeliveryOptions() {
		const result = await axios.get('/api/ajax/PortalGiftCardApi/GetGiftCardDeliveryOptions');
		switch (Object(Object(result).data).ResponseCode) {
			case 200:
			case 204:
				return result.data.Array.map(d => new GiftCardDeliveryOption(d));

			default:
				return false;
		}
	},

	/**
	 * Get the personal messages for portal gift cards.
	 * @returns {Promise<GiftCardPersonalMessage[]|boolean>} Array of delivery options, or `false` if fetching failed.
	 */
	async GetGiftCardPersonalMessages() {
		const result = await axios.get('/api/ajax/PortalGiftCardApi/GetGiftCardPersonalMessages');
		switch (Object(Object(result).data).ResponseCode) {
			case 200:
			case 204:
				return result.data.Array.map(d => new GiftCardPersonalMessage(d));

			default:
				return false;
		}
	},

	/**
	 * Get the available wrapping options for portal gift cards.
	 * @returns {Promise<GiftCardWrappingOption[]|boolean>} Array of wrapping options, or `false` if fetching failed.
	 */
	async GetGiftCardWrappingOptions() {
		const result = await axios.get('/api/ajax/PortalGiftCardApi/GetGiftCardWrappingOptions');
		switch (Object(Object(result).data).ResponseCode) {
			case 200:
			case 204:
				return result.data.Array.map(w => new GiftCardWrappingOption(w));

			default:
				return false;
		}
	}
};
